import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { motionPaused, setMotionPaused } from "./motion";
import "./site-footer.css";

// Pied de page commun (home, calculateur, pages modèle et prix GPU) : liens vers les
// outils, les cours et les pages les plus cherchées (maillage interne), mention d'affiliation.
// Listes écrites en dur, sans importer le moteur VRAM ni les prix : la home n'a pas à
// charger tout ce code pour huit liens. Les chemins suivent seo/vramModels et seo/gpuPrices.
// Mobile : les mêmes colonnes en accordéon (<details>), sinon 17 liens empilés de 44 px
// donnaient un pied de page de 770 px. Le CSS n'affiche qu'une des deux versions.

const COLUMNS: { title: string; label: string; links: [string, string][] }[] = [
  {
    title: "Tools", label: "Tools",
    links: [["LLM VRAM Calculator", "/llm-vram-calculator"], ["GPU rental prices", "/gpu-prices"]],
  },
  {
    title: "Learn", label: "Lessons",
    links: [
      ["Gradient Descent", "/gradient-descent"], ["Model Context Protocol", "/mcp"], ["AI/ML Architect", "/ai-ml-architect"],
      ["Build a Lakehouse", "/lakehouse"], ["Build Your Compiler", "/compiler"],
    ],
  },
  {
    title: "Model VRAM", label: "Model VRAM requirements",
    links: [
      ["Llama 3.3 70B", "/vram-requirements/llama-3-3-70b"], ["DeepSeek R1", "/vram-requirements/deepseek-r1"],
      ["gpt-oss 120B", "/vram-requirements/gpt-oss-120b"], ["Qwen3-Coder 30B-A3B", "/vram-requirements/qwen3-coder-30b-a3b"],
    ],
  },
  {
    title: "GPU prices", label: "GPU rental prices",
    links: [
      ["NVIDIA H100", "/gpu-price/h100"], ["NVIDIA H200", "/gpu-price/h200"],
      ["NVIDIA B200", "/gpu-price/b200"], ["RTX 4090", "/gpu-price/rtx-4090"],
    ],
  },
];

/** Met en pause le fond animé (WCAG 2.2.2). Masqué si le système demande déjà moins d'animations :
 *  le fond est alors immobile. État lu après l'hydratation : le HTML prérendu dit « Pause ». */
function MotionToggle() {
  const [paused, setPaused] = useState(false);
  useEffect(() => setPaused(motionPaused()), []);
  return (
    <button type="button" className="site-footer-motion" onClick={() => { setMotionPaused(!paused); setPaused(!paused); }}>
      {paused ? "Play" : "Pause"} background animation
    </button>
  );
}

export default function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <div>
          <p className="site-footer-name">StudioTV</p>
          <p>Free tools to size, rent and run open LLMs, and hands-on lessons on how they work.</p>
        </div>
        {COLUMNS.map((c) => (
          <nav key={c.title} aria-label={c.label}>
            <p className="site-footer-title">{c.title}</p>
            <ul>
              {c.links.map(([text, to]) => <li key={to}><Link to={to}>{text}</Link></li>)}
            </ul>
          </nav>
        ))}
      </div>
      <nav className="site-footer-mobile" aria-label="Footer">
        {COLUMNS.map((c) => (
          <details key={c.title} className="site-footer-acc">
            <summary>{c.title}<ChevronDown size={18} aria-hidden="true" /></summary>
            <ul>
              {c.links.map(([text, to]) => <li key={to}><Link to={to}>{text}</Link></li>)}
            </ul>
          </details>
        ))}
      </nav>
      <div className="site-footer-bottom">
        <p>Some rental links are affiliate links: StudioTV may earn a commission, at no extra cost to you.</p>
        <p>
          StudioTV, built by Vince ·{" "}
          <a href="https://github.com/StudioTV/" target="_blank" rel="noopener noreferrer">GitHub<span className="sr-only"> (opens in a new tab)</span></a> ·{" "}
          <a href="https://www.youtube.com/@StudioTV" target="_blank" rel="noopener noreferrer">YouTube<span className="sr-only"> (opens in a new tab)</span></a>
          <span className="site-footer-motion-wrap"> · <MotionToggle /></span>
        </p>
      </div>
    </footer>
  );
}
